import { useState, useEffect, useCallback } from 'react';

// ─── Shape ──────────────────────────────────────────────────────────────────
export interface AppConfig {
  dateFormat: string;
  timeFormat: '12h' | '24h';   // the Date & Time Zone page only offers these two
  timezone: string;
}

const EVENT_NAME = 'general-settings-changed';

const DEFAULT_CONFIG: AppConfig = {
  dateFormat: 'dd/MM/yyyy',
  timeFormat: '12h',
  timezone: 'Asia/Kolkata',
};

// ─── Helpers (synchronous, usable outside React) ────────────────────────────
// A cache of General Settings, which live in the database and are fetched on boot
// (useGeneralSettings). It used to be mirrored into localStorage; it is memory for the page now —
// the defaults apply for the moment before the fetch lands, and nothing is left in the browser.
let currentConfig: AppConfig = DEFAULT_CONFIG;
function readConfig(): AppConfig {
  return currentConfig;
}

/** Synchronous read — for use outside React (e.g. istDate.ts). */
export function getConfig(): AppConfig {
  return readConfig();
}

/** Write config and notify all listeners (instant reflection). */
export function setConfig(config: AppConfig): void {
  currentConfig = { ...DEFAULT_CONFIG, ...config };
  window.dispatchEvent(new Event(EVENT_NAME));
}

// ─── React hook — re-renders when config changes ────────────────────────────
export function useConfig(): AppConfig {
  const [config, setLocal] = useState<AppConfig>(readConfig);

  const refresh = useCallback(() => setLocal(readConfig()), []);

  useEffect(() => {
    window.addEventListener(EVENT_NAME, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(EVENT_NAME, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);

  return config;
}
